import { CloseIcon } from '../../../components/ui/icons';
import { dialogRadius } from '../../../components/ui/dialogSurface';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Location } from '../../../domain/location';
import { LocationFilter } from '../../locations/components/LocationFilter';

import {
  ItemFilterValues,
  LocationFilterScope,
  retainAvailableFilterKeys,
  retainAvailableLocationKeys,
} from '../useItemFilters';
import { TagFilter, inventoryTagKey } from '../../sharing/TagFilter';
import { InventoryTag } from '../../sharing/useCollectionTags';

const noTags: InventoryTag[] = [];

function ItemFilterForm({
  draft,
  locations,
  tags,
  showTags,
  showLocationDescription,
  showTitle,
  onChange,
  onCancel,
  onApply,
}: {
  draft: ItemFilterValues;
  locations: Location[];
  tags: InventoryTag[];
  showTitle: boolean;
  showTags: boolean;
  showLocationDescription: boolean;
  onChange: (value: ItemFilterValues) => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
      {showTitle && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Filter items</Typography>
          <IconButton aria-label="Close filters" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'grid',
            alignItems: 'start',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1.5,
            '& > *': { minWidth: 0 },
          }}
        >
          <LocationFilter
            value={draft.locations}
            onChange={(locations) => onChange({ ...draft, locations })}
            locations={locations}
            label="Location"
          />
          {showTags && (
            <TagFilter
              value={draft.tags}
              tags={tags}
              onChange={(tags) => onChange({ ...draft, tags })}
            />
          )}
          <TextField
            size="small"
            fullWidth
            label="Location name contains"
            value={draft.locationName}
            onChange={(event) =>
              onChange({ ...draft, locationName: event.target.value })
            }
            placeholder="Search location names…"
          />
          {showLocationDescription && (
            <TextField
              size="small"
              fullWidth
              label="Location description"
              value={draft.description}
              onChange={(event) =>
                onChange({ ...draft, description: event.target.value })
              }
              placeholder="Search location descriptions…"
            />
          )}
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() =>
            onChange({
              locations: [],
              tags: [],
              description: '',
              locationName: '',
            })
          }
        >
          Reset
        </Button>
        <Button variant="contained" onClick={onApply}>
          Apply filters
        </Button>
      </Stack>
    </Box>
  );
}

export function ItemFilterPanel({
  open,
  phoneLayout,
  applied,
  locationFilterScope,
  locations,
  tags = noTags,
  showTags = true,
  showLocationDescription = true,
  tagFilterScope,
  onClose,
  onApply,
}: {
  open: boolean;
  phoneLayout: boolean;
  applied: ItemFilterValues;
  locationFilterScope?: LocationFilterScope;
  locations: Location[];
  tags?: InventoryTag[];
  showTags?: boolean;
  showLocationDescription?: boolean;
  tagFilterScope?: LocationFilterScope;
  onClose: () => void;
  onApply: (filters: ItemFilterValues) => void;
}) {
  const [draft, setDraft] = useState<ItemFilterValues>(applied);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) setDraft(applied);
    wasOpen.current = open;
  }, [open, applied]);
  useEffect(() => {
    setDraft((previous) => {
      const remaining = retainAvailableLocationKeys(
        previous.locations,
        locations,
        locationFilterScope,
      );
      return remaining === previous.locations
        ? previous
        : { ...previous, locations: remaining };
    });
  }, [locations, locationFilterScope]);
  useEffect(() => {
    setDraft((previous) => {
      const remaining = retainAvailableFilterKeys(
        previous.tags,
        tags.map(inventoryTagKey),
        tagFilterScope,
      );
      return remaining === previous.tags
        ? previous
        : { ...previous, tags: remaining };
    });
  }, [tags, tagFilterScope]);

  const cancel = () => {
    setDraft(applied);
    onClose();
  };
  const apply = () => {
    onApply({
      ...draft,
      locations: retainAvailableLocationKeys(
        draft.locations,
        locations,
        locationFilterScope,
      ),
      tags: retainAvailableFilterKeys(
        draft.tags,
        tags.map(inventoryTagKey),
        tagFilterScope,
      ),
    });
  };
  const form = (
    <ItemFilterForm
      draft={draft}
      locations={locations}
      tags={tags}
      showTags={showTags}
      showLocationDescription={showLocationDescription}
      showTitle={phoneLayout}
      onChange={setDraft}
      onCancel={cancel}
      onApply={apply}
    />
  );

  if (phoneLayout) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={cancel}
        PaperProps={{
          sx: {
            maxHeight: '90dvh',
            borderRadius: dialogRadius(true),
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        {form}
      </Drawer>
    );
  }

  return (
    <Collapse in={open} unmountOnExit>
      <Paper variant="outlined">{form}</Paper>
    </Collapse>
  );
}
